import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Typography, Row, Col, Statistic, Skeleton, Alert } from "antd";
import { CloseOutlined, DollarOutlined, ThunderboltOutlined } from "@ant-design/icons";
import SbTag from "uiCore/SbTag/SbTag";
import SbButton from "uiCore/SbButton/SbButton";
import SbInfoTooltip from "uiCore/SbTooltip/SbInfoTooltip";
import SkillsBridgersIcon from "Components/SBLayoutIcon/SkillsBridgersIcon";
import PeoplePlusAiIcon from "Components/SBLayoutIcon/PeoplePlusAiIcon";
import useEntitlementsStore from "Store/useEntitlementsStore";
import { useHasAccess } from "Routes/hooks/useHasAccess";
import FilterBar from "./components/FilterBar";
import {
  Overlay,
  Panel,
  PanelHead,
  PanelHeadLeft,
  PanelDot,
  PanelContent,
  PanelContentInner,
  PageWrap,
  EmptyStateWrap,
  CenteredFlex,
  HeaderRow,
  ResetMarginTitle,
  InfoAlert,
  AboutDataText,
  DataQualityList,
  FilterBarSection,
  SkillsTagRow,
  SkillTag,
  MutedSubText,
  MutedSubTextSpaced,
} from "./Dashboard.styles";
import SplitProgress from "./components/SplitProgress";
import StatWidgetCard from "./components/StatWidgetCard";
import { FONT } from "./dashboard.constants";
import TotalPotentialPanel from "./panels/TotalPotentialPanel";
import CollaborationPanel from "./panels/CollaborationPanel";
import RoiPanel from "./panels/RoiPanel";
import TopSkillsPanel from "./panels/TopSkillsPanel";
import useWorkTransformationData from "./useWorkTransformationData";
import { formatUsd, formatHours, formatDdMmYyyy, hasValue } from "./dashboardData";
import { registerDashboardWidgetCloser, unregisterDashboardWidgetCloser } from "./dashboardWidgetHistory";

// "People+AI Collaboration Potential" matches the terminology used on the
// PAIC V2 Role Impact page ("Overall People+AI Collaboration Potential") —
// renamed here from "Human : AI Collaboration Potential" for consistency.
const WIDGET_CONFIG = {
  totalPotential: { title: "Total Potential", color: "#008080" },
  collaboration: { title: "People+AI Collaboration Potential", color: "#3F88CE" },
  roi: { title: "Return on AI Investments", color: "#2B3034" },
  // SkillsBridgersIcon's fill (white + teal) is baked into its own SVG, not
  // overridable via props — the previous light grey (#8790A4) badge left the
  // white lines with too little contrast to read. A darker grey fixes that.
  topSkills: { title: "Top Organisational Skills", color: "#565E70" },
};

const TOOLTIP_COLLABORATION =
  "Share of work across all roles that AI agents could handle (AI %) versus what stays with people (People %), based on the current filter.";
const TOOLTIP_POTENTIAL_VALUE =
  "Estimated dollar value unlocked by deploying AI agents across the filtered roles.";
const TOOLTIP_POTENTIAL_HOURS =
  "Estimated hours saved per year by deploying AI agents across the filtered roles.";

const Dashboard = () => {
  const { entitlements } = useEntitlementsStore();
  const { hasAccess } = useHasAccess();
  const [activeDim, setActiveDim] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [openWidget, setOpenWidget] = useState(null);
  const [filterHighlighted, setFilterHighlighted] = useState(false);
  const closedViaBackRef = useRef(false);
  const closedForNavigationRef = useRef(false);
  const filterBarSectionRef = useRef(null);
  const filterHighlightTimeoutRef = useRef(null);

  const hasDashboardAccess = hasAccess("dashboard", ["inprogress"]);
  const isFiltered = Boolean(activeDim) && activeIndex >= 0;
  // $ figures are a compensation figure — gated the same way PAIC V2 gates
  // it (Permission resource="people_plus_ai" actions={["view_comp"]} on Role
  // Impact / Group Agents / Agent Library): only roles with the view_comp
  // entitlement on people_plus_ai see it (HR Manager yes, HR Executive no).
  // The backend already keys this same distinction off people_plus_ai, so
  // this reuses it rather than a work_transformation-specific entitlement.
  const paiEntitlements = entitlements?.find((e) => e.name === "people_plus_ai")?.entitlements;
  const canViewDollarValue = !!paiEntitlements?.includes("view_comp");

  const {
    loading,
    roiLoading,
    noData,
    summary,
    totalPotentialUsd,
    totalPotentialHours,
    collaboration,
    roiLeaderboard,
    skills,
    dimensions,
    initialize,
    refetchForFilter,
  } = useWorkTransformationData();

  useEffect(() => {
    if (hasDashboardAccess) initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDashboardAccess]);

  useEffect(() => {
    if (!hasDashboardAccess || !dimensions) return;
    const value = isFiltered ? dimensions[activeDim]?.values[activeIndex] : undefined;
    refetchForFilter(activeDim, value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDim, activeIndex]);

  // Make browser Back close an expanded widget instead of leaving the Dashboard:
  // push a synthetic history entry while a widget is open and treat Back (popstate)
  // as a close. If the widget is closed some other way (X/overlay), unwind that
  // entry so a later Back still goes to the real previous page. Also register a
  // closer that sidebar/header navigation can call *before* it navigates away, so
  // that outbound push doesn't land on top of (and strand) our synthetic entry.
  useEffect(() => {
    if (!openWidget) return undefined;

    window.history.pushState({ dashboardWidget: true }, "");

    const handlePopState = () => {
      closedViaBackRef.current = true;
      setOpenWidget(null);
    };
    window.addEventListener("popstate", handlePopState);

    const closeForNavigation = (onReady) => {
      closedForNavigationRef.current = true;
      setOpenWidget(null);

      if (!window.history.state?.dashboardWidget) {
        onReady?.();
        return;
      }

      // Actually unwind the synthetic entry (not just relabel it) so the
      // caller's navigation pushes on top of a single, real Dashboard entry
      // instead of leaving a duplicate one behind. Wait for the resulting
      // popstate before letting the caller navigate, since history.back()
      // only takes effect asynchronously.
      const handleUnwound = () => {
        window.removeEventListener("popstate", handleUnwound);
        onReady?.();
      };
      window.addEventListener("popstate", handleUnwound);
      window.history.back();
    };
    registerDashboardWidgetCloser(closeForNavigation);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      unregisterDashboardWidgetCloser(closeForNavigation);
      if (
        !closedViaBackRef.current &&
        !closedForNavigationRef.current &&
        window.history.state?.dashboardWidget
      ) {
        window.history.back();
      }
      closedViaBackRef.current = false;
      closedForNavigationRef.current = false;
    };
  }, [openWidget]);

  const initialLoading = loading && !totalPotentialUsd;

  const headline = useMemo(
    () => ({
      usd: totalPotentialUsd?.total ?? 0,
      hours: totalPotentialHours?.total ?? 0,
      pct: collaboration?.total ?? 0,
      roi: roiLeaderboard?.aggregate_roi ?? 0,
    }),
    [totalPotentialUsd, totalPotentialHours, collaboration, roiLeaderboard],
  );

  const dataQualityNotes = summary?.data_quality?.notes || [];
  // Backend now returns both fields at these exact paths — no more guessing
  // at alternate field names or falling back to a root-level key.
  const lastUpdated = formatDdMmYyyy(summary?.data_quality?.last_updated);
  const jdCount = summary?.data_quality?.total_job_descriptions;
  const hasJdCount = hasValue(jdCount);

  const handleFilterGuideClick = () => {
    filterBarSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    clearTimeout(filterHighlightTimeoutRef.current);
    // Force a false->true transition (rather than just setting true, which
    // is a no-op re-render if a previous click's highlight is still active)
    // so the CSS pulse animation actually replays on a second click within
    // the highlight window.
    setFilterHighlighted(false);
    requestAnimationFrame(() => setFilterHighlighted(true));
    filterHighlightTimeoutRef.current = setTimeout(() => setFilterHighlighted(false), 2000);
  };

  useEffect(() => () => clearTimeout(filterHighlightTimeoutRef.current), []);

  const segmentLabel = isFiltered ? dimensions?.[activeDim]?.values[activeIndex] : null;
  const segmentDimLabel = isFiltered ? dimensions?.[activeDim]?.label : null;
  const skillsList = skills?.skills || [];
  const skillsCardSub =
    isFiltered && skills && !skills.fallback_used
      ? `Top skills for ${segmentLabel} (${skills.sample_size_roles} roles)`
      : isFiltered && skills?.fallback_used
      ? `Only ${skills.sample_size_roles} role${skills.sample_size_roles === 1 ? "" : "s"} in this run — showing org-wide skills instead`
      : "Most frequently required skills across all roles";

  const handleDimChange = (dim) => {
    setActiveDim(dim || "");
    setActiveIndex(-1);
  };

  const handleValueChange = (index) => setActiveIndex(Number(index));

  const handleClear = () => {
    setActiveDim("");
    setActiveIndex(-1);
  };

  const config = openWidget ? WIDGET_CONFIG[openWidget] : null;
  const heroValue =
    openWidget === "totalPotential"
      ? canViewDollarValue
        ? formatUsd(headline.usd)
        : `${formatHours(headline.hours)}/yr`
      : openWidget === "collaboration"
      ? `${Math.round(headline.pct)}%`
      : openWidget === "roi"
      ? `${headline.roi}x`
      : null;

  if (!hasDashboardAccess) {
    return (
      <EmptyStateWrap>
        <CenteredFlex align="center" justify="center">
          <Typography.Text type="secondary">
            You don't have access to the Work Transformation Dashboard.
          </Typography.Text>
        </CenteredFlex>
      </EmptyStateWrap>
    );
  }

  if (noData) {
    return (
      <EmptyStateWrap>
        <CenteredFlex align="center" justify="center">
          <Alert
            type="info"
            showIcon
            message="No dashboard data available yet"
            description="This org's Work Transformation data hasn't been computed yet. Run the initial data refresh, then reload this page."
          />
        </CenteredFlex>
      </EmptyStateWrap>
    );
  }

  return (
    <PageWrap>
      <HeaderRow>
        <ResetMarginTitle level={3}>
          Work Transformation Dashboard
        </ResetMarginTitle>
      </HeaderRow>

      <InfoAlert
        type="info"
        showIcon
        message="About this data"
        description={
          <AboutDataText>
            <div>
              {hasJdCount && (
                <>
                  Based on <strong>{jdCount}</strong> job descriptions analyzed
                  {" · "}
                </>
              )}
              {isFiltered ? (
                <>
                  Showing data for <strong>{segmentDimLabel}: {segmentLabel}</strong>
                </>
              ) : (
                <>
                  Org-wide data for PAIC evaluations across all groups —{" "}
                  <Typography.Link onClick={handleFilterGuideClick}>
                    apply a filter below to narrow this down
                  </Typography.Link>
                </>
              )}
              {lastUpdated && (
                <>
                  {" · "}Last updated: <strong>{lastUpdated}</strong>
                </>
              )}
            </div>
            {dataQualityNotes.length > 0 && (
              <DataQualityList>
                {dataQualityNotes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </DataQualityList>
            )}
          </AboutDataText>
        }
      />

      <FilterBarSection ref={filterBarSectionRef}>
        <FilterBar
          dimensions={dimensions}
          activeDim={activeDim}
          activeIndex={activeIndex}
          onDimChange={handleDimChange}
          onValueChange={handleValueChange}
          onClear={handleClear}
          loading={loading && !initialLoading}
          highlighted={filterHighlighted}
        />
      </FilterBarSection>

      <Row gutter={[12, 10]} align="stretch">
        <Col xs={24} md={12}>
          <StatWidgetCard
            icon={<SkillsBridgersIcon width={20} height={20} />}
            color={WIDGET_CONFIG.topSkills.color}
            label="Top Organisational Skills"
            sub={skillsCardSub}
            footHint="Org-wide · Top 10 skills"
            onClick={() => setOpenWidget("topSkills")}
          >
            {initialLoading ? (
              <Skeleton active paragraph={{ rows: 1 }} title={false} />
            ) : (
              <SkillsTagRow>
                {skillsList.slice(0, 5).map((s, i) => (
                  <SkillTag key={s.skill} color="processing">
                    {s.skill}
                  </SkillTag>
                ))}
              </SkillsTagRow>
            )}
          </StatWidgetCard>
        </Col>

        <Col xs={24} md={12}>
          <StatWidgetCard
            icon={<PeoplePlusAiIcon width={16} height={16} />}
            color={WIDGET_CONFIG.collaboration.color}
            label={<SbInfoTooltip content={TOOLTIP_COLLABORATION}>People+AI Collaboration Potential</SbInfoTooltip>}
            sub="Automation potential across all roles"
            footHint="Click to explore breakdown"
            onClick={() => setOpenWidget("collaboration")}
          >
            {initialLoading ? (
              <Skeleton active paragraph={{ rows: 1 }} title={{ width: 80 }} />
            ) : (
              <>
                <Statistic
                  value={Math.round(headline.pct)}
                  suffix="%"
                  valueStyle={{ color: WIDGET_CONFIG.collaboration.color, fontWeight: 700, fontSize: FONT.stat }}
                />
                <SplitProgress aiPct={headline.pct} />
              </>
            )}
          </StatWidgetCard>
        </Col>

        <Col xs={24} md={12}>
          <StatWidgetCard
            icon={<DollarOutlined />}
            color={WIDGET_CONFIG.totalPotential.color}
            label="Total Potential"
            sub="Estimated potential value unlocked from AI Agents"
            footHint="Click to explore breakdown"
            onClick={() => setOpenWidget("totalPotential")}
          >
            {initialLoading ? (
              <Skeleton active paragraph={{ rows: 1 }} title={{ width: 120 }} />
            ) : canViewDollarValue ? (
              <>
                <MutedSubText>
                  <SbInfoTooltip content={TOOLTIP_POTENTIAL_VALUE}>Potential value created by AI</SbInfoTooltip>
                </MutedSubText>
                <Statistic
                  value={headline.usd}
                  formatter={(v) => formatUsd(v)}
                  valueStyle={{ color: WIDGET_CONFIG.totalPotential.color, fontWeight: 700, fontSize: FONT.stat }}
                />
                <MutedSubTextSpaced>
                  <SbInfoTooltip content={TOOLTIP_POTENTIAL_HOURS}>Potential hours saved by AI</SbInfoTooltip>
                </MutedSubTextSpaced>
                <SkillTag color="processing">
                  {formatHours(headline.hours)}/yr
                </SkillTag>
              </>
            ) : (
              <>
                <MutedSubText>
                  <SbInfoTooltip content={TOOLTIP_POTENTIAL_HOURS}>Potential hours saved by AI</SbInfoTooltip>
                </MutedSubText>
                <Statistic
                  value={headline.hours}
                  formatter={(v) => formatHours(v)}
                  valueStyle={{ color: WIDGET_CONFIG.totalPotential.color, fontWeight: 700, fontSize: FONT.stat }}
                />
                <SkillTag color="processing">
                  per year
                </SkillTag>
              </>
            )}
          </StatWidgetCard>
        </Col>

        <Col xs={24} md={12}>
          <StatWidgetCard
            icon={<ThunderboltOutlined />}
            color={WIDGET_CONFIG.roi.color}
            label="Return on AI Investments"
            sub="Total return on AI investment"
            footHint="Click to see full ranking + breakdown"
            onClick={() => setOpenWidget("roi")}
          >
            {initialLoading || roiLoading ? (
              <Skeleton active paragraph={false} title={{ width: 80 }} />
            ) : (
              <Statistic
                value={headline.roi}
                suffix="x"
                valueStyle={{ color: WIDGET_CONFIG.roi.color, fontWeight: 700, fontSize: FONT.stat }}
              />
            )}
          </StatWidgetCard>
        </Col>
      </Row>

      {openWidget &&
        createPortal(
          <>
            <Overlay onClick={() => setOpenWidget(null)} />
            <Panel>
              <PanelHead>
                <PanelHeadLeft>
                  <PanelDot $color={config.color} />
                  <ResetMarginTitle level={4}>
                    {config.title}
                  </ResetMarginTitle>
                  {heroValue && <SbTag color="processing">{heroValue}</SbTag>}
                </PanelHeadLeft>
                <SbButton
                  buttonType="secondary"
                  shape="circle"
                  icon={<CloseOutlined />}
                  onClick={() => setOpenWidget(null)}
                />
              </PanelHead>
              <PanelContent>
                <PanelContentInner>
                  {openWidget === "totalPotential" && (
                    <TotalPotentialPanel
                      totalPotentialUsd={totalPotentialUsd}
                      totalPotentialHours={totalPotentialHours}
                      canViewDollarValue={canViewDollarValue}
                    />
                  )}
                  {openWidget === "collaboration" && <CollaborationPanel collaboration={collaboration} />}
                  {openWidget === "roi" && <RoiPanel agents={roiLeaderboard?.agents} />}
                  {openWidget === "topSkills" && (
                    <TopSkillsPanel skillsData={skills} segmentLabel={segmentLabel} segmentDimLabel={segmentDimLabel} />
                  )}
                </PanelContentInner>
              </PanelContent>
            </Panel>
          </>,
          document.body,
        )}
    </PageWrap>
  );
};

export default Dashboard;
